import React, { useState, useContext, useEffect } from 'react';
import { CountdownTimer } from './CountdownTimer';
import { urlEncodeText, getSecondsToTime } from '../../helpers';
import { useHistory } from 'react-router-dom';
import { AppDataContext, VLink } from "../AppDataContext";
import useNetwork from '../../useNetwork';
import CurrencyFormat from 'react-currency-format';

export function LotTile(props) {
    const context = useContext(AppDataContext);
    const { loading, networkError, netRequest, netRenderError } = useNetwork(true);

    const history = useHistory();

    const [closeTime, setCloseTime] = useState(props.lot.closeTime);
    const [isWidthdrawn, setIsWidthdrawn] = useState(props.lot.isWidthdrawn);

    // now compute the corrected seconds to close
    const localSecondsToClose = getSecondsToTime(props.lot.closeTime, props.gmt, props.systemSecondsToCloseDelta);
    const [secondsToClose, setSecondsToClose] = useState(localSecondsToClose);
    const [isClosed, setIsClosed] = useState(null);

    const [currentBid, setCurrentBid] = useState(props.currentBid);
    const [currentBidTime, setCurrentBidTime] = useState(props.currentBid?.bidTime);
    const [currentBidder, setCurrentBidder] = useState(props.currentBid?.paddleNumber);
    const [isThisUser, setIsThisUser] = useState(props.currentBid?.isThisUser);
    const [bidResultEnum, setBidResultEnum] = useState("");

    const calcBidAmount = props.currentBid ? props.currentBid.currentAmount + props.currentBid.incrementAmount : props.lot.startPrice;
    const [currentBidAmount, setCurrentBidAmount] = useState(calcBidAmount);
    const [currentMaxAmount, setCurrentMaxAmount] = useState(props.currentBid?.currentMaxAmount);
    const [currentIncrement, setCurrentIncrement] = useState(props.currentBid?.incrementAmount);
    const [bidAmount, setBidAmount] = useState(calcBidAmount);
    const [isFavorite, setIsFavorite] = useState(props.lot.isFavorite);
    const [hasBid, setHasBid] = useState(props.lot.hasBid);
    const [fadeFavorite, setFadeFavorite] = useState(false);

    const [bidEventTimer, setBidEventTimer] = useState(0);

    // when the lot changes, e.g. when they log in when trying to bid, update the favorites 
    useEffect(() => {
        setIsFavorite(props.lot.isFavorite)
        setHasBid(props.lot.hasBid)
    }, [props.lot]);


    const toggleFavorite = async () => {
        // initially toggle to the new state
        const currentlyIsFavorite = isFavorite;
        setIsFavorite(!isFavorite);

        const url = 'api/site/setfavorite';
        const postData = {
            secureId: props.lot.secureId,
            isFavorite: !isFavorite,
        };

        const response = await netRequest(url, postData);

        if (response.data != null) {
            // failed?  put it back
            setIsFavorite(currentlyIsFavorite);
        }

        if (props.isFavorite) {
            if (currentlyIsFavorite) {
                // fade the item
                setFadeFavorite(true);
            } else {
                setFadeFavorite(false);
            }
        }
    }

    // every time a bid comes in
    useEffect(() => {
        if (props.currentBid) {

            // only update from broadcast when the time is later then the current bid we're showing (or we don't have a current bid, yet)
            // i suppose it can happen if two bids happen in close succession and, for some
            // reason SignalR sends them back in the wrong order.  We always want to keep the latest
            if (!props.currentBid.isBroadcast || !currentBidTime || currentBidTime < props.currentBid.bidTime || props.currentBid.isRetract) {

                setCurrentBid(props.currentBid);

                const localSecondsToClose = getSecondsToTime(props.currentBid.closeTime, props.gmt, isNaN(props.SystemSecondsToCloseDelta) ? 0 : props.SystemSecondsToCloseDelta);
                setSecondsToClose(localSecondsToClose);
                setCloseTime(props.currentBid.closeTime);

                let bidAmount = props.currentBid.currentAmount + props.currentBid.incrementAmount;
                setBidAmount(bidAmount);

                setCurrentBidAmount(props.currentBid.currentAmount);
                setCurrentIncrement(props.currentBid.incrementAmount);
                setCurrentBidTime(props.currentBid.bidTime);
                setBidResultEnum(props.currentBid.bidResultEnum);

                if (props.currentBid.isBroadcast === true) {
                    // trigger a notification that the bid was broadcast
                    setBidEventTimer(3);

                    // an outbid by max as a broadcast means leave the current bidder and thisUser alone
                    // for the user with the current max bid, they still get to see their max, and for
                    // other users, they weren't seeing it, anyway
                    // when a bid comes through with this users paddle number, it must have come either from
                    // another browser/tab, or a Pending Bid
                    if ((props.currentBid.bidResultEnum != "BidOutbidByMax" &&
                        props.currentBid.paddleNumber != props.lot.auctionSession.auction.paddleNumber) ||
                        props.currentBid.isRetract) {
                        setCurrentMaxAmount(props.currentBid.currentMaxAmount);
                        setCurrentBidder(props.currentBid.paddleNumber);
                        setIsThisUser(false);
                    }
                } else {
                    // isBroadcast is false as a result of this user's bid
                    // isBroadcast is undefined when we come in the first time filling in the lot.
                    // This user, the bidder's name and the max (whether user can see it or not) is set in these cases
                    setCurrentBidder(props.currentBid.paddleNumber);
                    setCurrentMaxAmount(props.currentBid.currentMaxAmount);
                    setIsThisUser(props.currentBid.isThisUser);
                    if (props.currentBid.isBroadcast === false)
                        setHasBid(true);
                }
            }
        }
    }, [props.currentBid]);

    // every time a tick comes in (prop change from the page we're contained in), reset the seconds to close based
    // on the delta that we computed, above.
    useEffect(() => {
        const theSecondsToClose = getSecondsToTime(closeTime, props.gmt, props.systemSecondsToCloseDelta);

        // when the seconds to close hits zero, notify anyone listening that this tile just closed
        if (theSecondsToClose <= 0 && isClosed == false && props.notifyClosed) {
            props.notifyClosed(props.lot);
            setIsClosed(true);
        }
        if (theSecondsToClose > 0) {
            setIsClosed(false);
        }
        setSecondsToClose(theSecondsToClose);

        if (bidEventTimer > 0)
            setBidEventTimer(bidEventTimer - 1);
    }, [props.gmt]);

    const placeBid = async () => {
        await props.onPlaceBid(props.lot);
    }

    let classes = "col-6 col-md-4 col-lg-3 mb-4";
    let classesItem = "auction--item ";
    if (bidEventTimer > 0)
        classesItem += "auction--item__changed--newbid";

    let style = {}
    // opacity 1 second transition is for removing the favorite so it remains on the screen faded.
    if (props.isFavorite) {
        style.transition = 'opacity 1s';
        if (fadeFavorite === true) {
            classes += " opacity-50";
        }
    }

    let imageStyle = {}
    // closeTime of null is just the "Preview" mode.  showClosed property allows closed lots to be shown and not hidden
    // as happens in a live auction as the lot closes
    if (closeTime != null && !props.showClosed) {
        if (secondsToClose < 60)
            classesItem = "auction--item auction--item__changed--expiring";
        if (secondsToClose < 10 && secondsToClose > -4)
            imageStyle = { opacity: secondsToClose / 10, transition: 'opacity 1s' };
        if (secondsToClose <= -2)
            style = { maxWidth: "0px", transition: 'max-width 2.5s', opacity: 0 };
        if (secondsToClose <= -4)
            classes = "d-none";
    }

    const lotUrl = '/auction/' + props.auctionId + '/lot/' + props.lot.secureId + '/' + urlEncodeText(props.lot.title);
    
    return (
        <>
            <div className={classes} style={style}>
                <div class={classesItem}>
                    <div class="auction--item__image auction--item__lot--img position-relative" style={imageStyle}>
                        {!isWidthdrawn && closeTime != null &&
                            <>
                            {context.isLoggedIn ?
                                <button className={"btn btn-fav position-absolute" + (isFavorite == true ? " active" : "")} onClick={toggleFavorite}>
                                    <i class="bi bi-star"></i>
                                    <i class="bi bi-star-fill"></i>
                                </button>
                                :
                                <button class="btn btn-fav position-absolute" data-bs-toggle="modal" data-bs-target="#loginModal">
                                    <i class="bi bi-star"></i>
                                    <i class="bi bi-star-fill"></i>
                                </button>
                            }
                            </>
                        }
                        <span class="position-absolute auction-labels">
                            {isThisUser &&
                                <label class="me-1 mb-1 auction-label highest">
                                    {secondsToClose <= -4 ? "Won!" : "High Bid"}
                                </label>
                            }
                            {!isThisUser && hasBid &&
                                <label class="me-1 mb-1 auction-label outbidded">Outbid</label>
                            }
                        </span>
                        <VLink to={lotUrl} style={{ textDecoration: 'none' }} onClick={props?.onLinkClicked}>
                            {props.lot.inventoryItemImageSecureId == null ?
                                <img src="images/noimage.png" title="" alt="" class="w-100" />
                                :
                                <img src={'api/site/InventoryImage/' + props.lot.inventoryItemImageSecureId + '/450'} title={props.lot.title} alt={props.lot.title} class="w-100" />
                            }
                        </VLink>
                    </div>
                    <div class="auction--item__content pt-1">
                        <div class="auction--item__content--details px-3 pb-2">
                            <h4 class="auction--item__content--title">
                                <VLink to={lotUrl} style={{ textDecoration: 'none' }} class="auction--item__content--link"
                                    title={props.lot.title} onClick={props?.onLinkClicked}>
								#{props.lot.lotNumber}{props.lot.pieces && <> ({props.lot.pieces}pc) </> } {props.lot.title}
                                </VLink>
                            </h4>
                            <div class="auction-info__item price mb-1">
                                {(closeTime != null && context.isLoggedIn == false && secondsToClose <= -4) ?
                                        props.currentBid ?
                                            <h6 class="text-center">
                                                <a class="nav-link" href="#" data-bs-toggle="modal" data-bs-target="#loginOrRegisterModal" title="Log In">Log in to see the price</a>
                                            </h6>
                                        :
                                            <h6 class="text-center">&nbsp;</h6>
                                    :
                                        <h4 class="text-center">
                                            <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={currentBidAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
                                        </h4>
                                }
                            </div>
                            {(closeTime != null && isWidthdrawn != true && secondsToClose > -2) &&
                                <button class="btn btn-fill btn-primary w-100" onClick={placeBid} disabled={closeTime == null || isWidthdrawn || secondsToClose <= 0}>
                                    {props.lot.pendingBidAmount != null || isThisUser === true ? "Update Max" : "Place Bid"}
                                </button>
                            }
                        </div>
                        {props.showAuctionInfo &&
                            <div class="px-3">
                        		<div class="auction-info__item mb-1">
									<i class="bi bi-geo-alt me-1"></i>
                                    <VLink class="text-uppercase" to={"/auction/" + props.lot.auctionSession.secureId + '/' + urlEncodeText(props.lot.auctionSession.title)} title={props.lot.auctionSession.title}>{props.lot.auctionSession.title}</VLink>
								</div>
								<div class="auction-info__item mb-1">
                                    <i class="bi bi-geo-alt me-1"></i>
                                    {props.lot.auctionSession.auction.city}, {props.lot.auctionSession.auction.state}
                                </div>
                            </div>
                        }
                        <div class="auction--item__content--actions">
                            <div class="row py-2 px-0 m-0">
                                {isWidthdrawn ?
                                    <div class="col text-center badge badge-danger">Widthdrawn</div>
                                    :
                                    closeTime == null ?
                                        <span class="col text-center badge badge-warning">Preview</span>
                                    :
                                        secondsToClose <= -2 ?
                                            props.currentBid == null ?
                                                <span class="col text-center badge badge-warning">Passed Lot</span>
                                                :
                                                <span class="col text-center badge badge-warning">Sold</span>
                                            :
                                            <>
                                                <CountdownTimer secondsToClose={secondsToClose} />
                                            </>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
		</>
    );
}
